<template>
  <v-dialog
    v-model="dialog"
    max-width="960px"
    persistent
    transition="dialog-top-transition"
  >
    <v-card class="elevation-2">
      <v-toolbar color="grey-lighten-4" class="px-3">
        <div class="font-weight-bold text-subtitle-1">Crop Image</div>
        <v-spacer />
        <v-btn icon variant="text" @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-divider></v-divider>
      <v-card-text class="pa-4">
        <v-alert
          type="info"
          variant="tonal"
          density="compact"
          class="mb-4"
          border="start"
          color="primary"
        >
          Drag to move • Aspect ratio fixed to 16:9
        </v-alert>

        <v-sheet
          class="pa-4 rounded-lg cropper-surface"
          color="grey-lighten-5"
          border
        >
          <div class="d-flex justify-center align-center" style="min-height: 420px">
            <div v-if="imageSrc" class="cropper-container">
              <div class="cropper-wrapper" ref="cropperWrapper">
                <img
                  ref="cropperImage"
                  :src="imageSrc"
                  @load="onImageLoad"
                  class="cropper-image"
                />

                <div
                  v-if="cropBox"
                  class="crop-box"
                  :style="cropBoxStyle"
                  @mousedown="startDrag"
                >
                  <div class="grid-lines">
                    <div class="grid-line grid-line-vertical" style="left: 33.33%"></div>
                    <div class="grid-line grid-line-vertical" style="left: 66.66%"></div>
                    <div class="grid-line grid-line-horizontal" style="top: 33.33%"></div>
                    <div class="grid-line grid-line-horizontal" style="top: 66.66%"></div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-center py-6">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <div class="text-caption text-medium-emphasis mt-2">Loading image…</div>
            </div>
          </div>
        </v-sheet>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn color="grey" variant="text" @click="closeDialog">Cancel</v-btn>
        <v-btn color="primary" variant="flat" @click="applyCrop" :loading="isApplying">
          <v-icon size="small" class="mr-1">mdi-check</v-icon>
          Apply Crop
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "ImageCropperDialog",
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    imageFile: {
      type: File,
      default: null,
    },
  },
  emits: ["update:modelValue", "cropped", "cancel"],
  data() {
    return {
      imageSrc: null,
      cropBox: null,
      isDragging: false,
      isResizing: false,
      resizeDirection: null,
      dragStart: { x: 0, y: 0 },
      cropStart: { x: 0, y: 0, width: 0, height: 0 },
      imageRect: null,
      isApplying: false,
      aspectRatio: 16 / 9,
    };
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    cropBoxStyle() {
      if (!this.cropBox) return {};
      return {
        left: `${this.cropBox.x}px`,
        top: `${this.cropBox.y}px`,
        width: `${this.cropBox.width}px`,
        height: `${this.cropBox.height}px`,
      };
    },
  },
  watch: {
    modelValue(newVal) {
      if (newVal && this.imageFile) {
        this.loadImage();
      } else if (!newVal) {
        this.resetState();
      }
    },
    imageFile(newFile) {
      if (newFile && this.modelValue) {
        this.loadImage();
      }
    },
  },
  mounted() {
    if (this.modelValue && this.imageFile) {
      this.loadImage();
    }
    document.addEventListener("mousemove", this.onMouseMove);
    document.addEventListener("mouseup", this.onMouseUp);
  },
  beforeUnmount() {
    document.removeEventListener("mousemove", this.onMouseMove);
    document.removeEventListener("mouseup", this.onMouseUp);
    this.revokeImage();
  },
  methods: {
    resetState() {
      this.cropBox = null;
      this.imageRect = null;
      this.isApplying = false;
      this.revokeImage();
      this.imageSrc = null;
    },
    revokeImage() {
      if (this.imageSrc && this.imageSrc.startsWith("blob:")) {
        URL.revokeObjectURL(this.imageSrc);
      }
    },
    loadImage() {
      if (this.imageFile && this.imageFile.type.startsWith("image/")) {
        this.revokeImage();
        this.imageSrc = URL.createObjectURL(this.imageFile);
      }
    },
    onImageLoad() {
      this.$nextTick(() => {
        const img = this.$refs.cropperImage;
        const wrapper = this.$refs.cropperWrapper;
        if (img && wrapper) {
          const imgRect = img.getBoundingClientRect();
          const wrapperRect = wrapper.getBoundingClientRect();

          this.imageRect = {
            x: imgRect.left - wrapperRect.left,
            y: imgRect.top - wrapperRect.top,
            width: imgRect.width,
            height: imgRect.height,
          };
          this.applyAspectRatio();
        }
      });
    },
    startDrag(event) {
      if (this.isResizing || !this.cropBox) return;
      this.isDragging = true;
      const imgRect = this.$refs.cropperImage?.getBoundingClientRect();
      if (imgRect) {
        this.dragStart = {
          x: event.clientX - imgRect.left - this.cropBox.x,
          y: event.clientY - imgRect.top - this.cropBox.y,
        };
      }
    },
    startResize(direction, event) {
      event.stopPropagation();
      if (!this.cropBox) return;
      this.isResizing = true;
      this.resizeDirection = direction;
      this.cropStart = { ...this.cropBox };
      const imgRect = this.$refs.cropperImage?.getBoundingClientRect();
      this.dragStart = {
        x: event.clientX - (imgRect ? imgRect.left : 0),
        y: event.clientY - (imgRect ? imgRect.top : 0),
      };
    },
    onMouseMove(event) {
      if (!this.cropBox || !this.imageRect) return;
      if (this.isDragging) {
        const imgRect = this.$refs.cropperImage?.getBoundingClientRect();
        if (!imgRect) return;
        let newX = event.clientX - imgRect.left - this.dragStart.x;
        let newY = event.clientY - imgRect.top - this.dragStart.y;
        newX = Math.max(0, Math.min(newX, this.imageRect.width - this.cropBox.width));
        newY = Math.max(0, Math.min(newY, this.imageRect.height - this.cropBox.height));
        this.cropBox = { ...this.cropBox, x: newX, y: newY };
      } else if (this.isResizing) {
        this.handleResize(event);
      }
    },
    handleResize(event) {
      if (!this.cropBox || !this.imageRect) return;
      const imgRect = this.$refs.cropperImage?.getBoundingClientRect();
      if (!imgRect) return;
      const currentX = event.clientX - imgRect.left;
      const currentY = event.clientY - imgRect.top;
      const deltaX = currentX - this.dragStart.x;
      const deltaY = currentY - this.dragStart.y;
      let { x, y, width, height } = this.cropStart;
      const ratio = this.aspectRatio;
      const minSize = 50;

      const applyBounds = () => {
        width = Math.max(minSize, Math.min(width, this.imageRect.width - x));
        height = Math.max(minSize, Math.min(height, this.imageRect.height - y));
        x = Math.max(0, Math.min(x, this.imageRect.width - width));
        y = Math.max(0, Math.min(y, this.imageRect.height - height));
      };

      const syncHeight = () => {
        height = width / ratio;
      };
      const syncWidth = () => {
        width = height * ratio;
      };

      switch (this.resizeDirection) {
        case "e":
          width = this.cropStart.width + deltaX;
          syncHeight();
          break;
        case "w":
          width = this.cropStart.width - deltaX;
          x = this.cropStart.x + deltaX;
          syncHeight();
          break;
        case "s":
          height = this.cropStart.height + deltaY;
          syncWidth();
          break;
        case "n":
          height = this.cropStart.height - deltaY;
          y = this.cropStart.y + deltaY;
          syncWidth();
          break;
        default:
          // corners
          if (this.resizeDirection.includes("e")) {
            width = this.cropStart.width + deltaX;
            syncHeight();
          }
          if (this.resizeDirection.includes("w")) {
            width = this.cropStart.width - deltaX;
            x = this.cropStart.x + deltaX;
            syncHeight();
          }
          if (this.resizeDirection.includes("s")) {
            height = width / ratio;
          }
          if (this.resizeDirection.includes("n")) {
            height = width / ratio;
            y = this.cropStart.y + (this.cropStart.height - height);
          }
          break;
      }

      applyBounds();
      this.cropBox = { x, y, width, height };
    },
    onMouseUp() {
      this.isDragging = false;
      this.isResizing = false;
      this.resizeDirection = null;
    },
    applyCrop() {
      if (!this.cropBox || !this.$refs.cropperImage || !this.imageFile) return;
      this.isApplying = true;
      const img = this.$refs.cropperImage;
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const imgRect = img.getBoundingClientRect();
      const scaleX = img.naturalWidth / imgRect.width;
      const scaleY = img.naturalHeight / imgRect.height;
      const cropX = this.cropBox.x * scaleX;
      const cropY = this.cropBox.y * scaleY;
      const cropWidth = this.cropBox.width * scaleX;
      const cropHeight = this.cropBox.height * scaleY;
      canvas.width = cropWidth;
      canvas.height = cropHeight;
      ctx.drawImage(img, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            this.isApplying = false;
            return;
          }
          const croppedFile = new File([blob], this.imageFile.name, {
            type: this.imageFile.type || "image/png",
          });
          this.$emit("cropped", croppedFile);
          this.closeDialog(false);
        },
        this.imageFile.type || "image/png",
        0.95
      );
    },
    closeDialog(isCancelled = true) {
      this.dialog = false;
      if (isCancelled && !this.isApplying) {
        this.$emit("cancel");
      }
      this.resetState();
      this.isApplying = false;
    },
    applyAspectRatio() {
      if (!this.imageRect) return;
      const targetRatio = this.aspectRatio;
      const imageWidth = this.imageRect.width;
      const imageHeight = this.imageRect.height;
      let cropWidth = imageWidth;
      let cropHeight = cropWidth / targetRatio;
      if (cropHeight > imageHeight) {
        cropHeight = imageHeight;
        cropWidth = cropHeight * targetRatio;
      }
      cropWidth = Math.min(cropWidth, imageWidth);
      cropHeight = Math.min(cropHeight, imageHeight);
      const cropX = Math.max(0, (imageWidth - cropWidth) / 2);
      const cropY = Math.max(0, (imageHeight - cropHeight) / 2);
      this.cropBox = {
        x: cropX,
        y: cropY,
        width: cropWidth,
        height: cropHeight,
      };
    },
  },
};
</script>

<style scoped>
.cropper-container {
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}
.cropper-wrapper {
  position: relative;
  display: inline-block;
}
.cropper-image {
  max-width: 100%;
  max-height: 350px;
  display: block;
  user-select: none;
  border-radius: 8px;
  /* box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1); */
}
.cropper-surface {
  border-style: dashed;
  border-color: #e0e0e0;
}
.grid-lines {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}
.grid-line {
  position: absolute;
  background: rgba(255, 255, 255, 0.6);
  /* box-shadow: 0 0 2px rgba(0, 0, 0, 0.3); */
}
.grid-line-vertical {
  width: 1px;
  height: 100%;
}
.grid-line-horizontal {
  width: 100%;
  height: 1px;
}
.crop-box {
  position: absolute;
  border: 3px solid #2196f3;
  background: transparent;
  cursor: move;
  box-sizing: border-box;
  z-index: 2;
  /* box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5), inset 0 0 0 1px rgba(255, 255, 255, 0.3); */
}
.crop-box::before {
  content: "";
  position: absolute;
  top: -1px;
  left: -1px;
  right: -1px;
  bottom: -1px;
  border: 1px dashed rgba(255, 255, 255, 0.8);
  pointer-events: none;
}
.crop-handle {
  position: absolute;
  background: #2196f3;
  border: 3px solid white;
  /* box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3); */
  z-index: 3;
  transition: transform 0.1s ease, background 0.1s ease;
}
.crop-handle:hover {
  transform: scale(1.2);
  background: #1976d2;
}
.handle-inner {
  width: 100%;
  height: 100%;
  border-radius: 50%;
}
.crop-handle-nw,
.crop-handle-ne,
.crop-handle-sw,
.crop-handle-se {
  width: 20px;
  height: 20px;
  border-radius: 50%;
}
.crop-handle-nw {
  top: -10px;
  left: -10px;
  cursor: nw-resize;
}
.crop-handle-ne {
  top: -10px;
  right: -10px;
  cursor: ne-resize;
}
.crop-handle-sw {
  bottom: -10px;
  left: -10px;
  cursor: sw-resize;
}
.crop-handle-se {
  bottom: -10px;
  right: -10px;
  cursor: se-resize;
}
.crop-handle-n,
.crop-handle-s {
  width: 40px;
  height: 8px;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 4px;
}
.crop-handle-w,
.crop-handle-e {
  width: 8px;
  height: 40px;
  top: 50%;
  transform: translateY(-50%);
  border-radius: 4px;
}
.crop-handle-n {
  top: -4px;
  cursor: n-resize;
}
.crop-handle-s {
  bottom: -4px;
  cursor: s-resize;
}
.crop-handle-w {
  left: -4px;
  cursor: w-resize;
}
.crop-handle-e {
  right: -4px;
  cursor: e-resize;
}
</style>

